import { useState } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import { authClient } from '../../utils/amplifyClient';
import { createCompanyNote as createCompanyNoteMutation } from '../../graphql/mutations';
import { ReportBugModal } from './ReportBugModal';
import { RequestFeatureModal } from './RequestFeatureModal';
import '../../css/GetHelpContent.css';
import {
    AccordionCard,
    Btn,
    Card,
    IconAlertCircle,
    IconAlertTriangle,
    IconBookReading,
    IconEmail,
    IconPhone,
    PageHeader,
    TabNav,
    getTabPanelProps,
} from '../ui';
import type { TabNavItem } from '../ui';

const HELP_TAB_NAV_ID = 'get-help';

type TabId = 'contact-support' | 'module-guides' | 'faq-docs';

const TABS: (TabNavItem & { id: TabId })[] = [
    {
        id: 'contact-support',
        ariaLabel: 'Contact Support',
        label: (
            <>
                <IconPhone />
                Contact Support
            </>
        ),
    },
    {
        id: 'module-guides',
        ariaLabel: 'Module Guides',
        label: (
            <>
                <IconBookReading />
                Module Guides
            </>
        ),
    },
    {
        id: 'faq-docs',
        ariaLabel: 'FAQ & Docs',
        label: (
            <>
                <IconAlertCircle />
                FAQ & Docs
            </>
        ),
    },
];

const VALID_TAB_IDS = new Set<string>(TABS.map((t) => t.id));

// ===== Module Guides Data (placeholders) =====
const MODULE_GUIDES = [
    {
        id: 'equipment',
        title: 'Equipment Module',
        description: 'Create and edit equipment, assign inspection forms, and manage equipment profiles.',
        steps: 5,
        image: '',
    },
    {
        id: 'tire-maintenance',
        title: 'Tire Maintenance',
        description: 'Track your entire tire inventory, manage tire inspections, view tread & more.',
        steps: 4,
        image: '',
    },
    {
        id: 'form-builder',
        title: 'Form Builder',
        description: 'Create custom inspection forms with sections, items, and customized logs.',
        steps: 6,
        image: '',
    },
    {
        id: 'pm',
        title: 'Preventive Maintenance',
        description: 'Configure PM schedules, track service history, and manage work orders.',
        steps: 5,
        image: '',
    },
    {
        id: 'safety-training',
        title: 'Safety & Training',
        description: 'Build training plans, assign topics, and track employee completion.',
        steps: 7,
        image: '',
    },
    {
        id: 'cost-tracking',
        title: 'Cost Tracking',
        description: 'Record expenses, track costs by equipment, and generate cost reports.',
        steps: 4,
        image: '',
    },
    {
        id: 'reports',
        title: 'Reports',
        description: 'Run standard reports, build custom reports, and export data.',
        steps: 5,
        image: '',
    },
    {
        id: 'users',
        title: 'Users & Permissions',
        description: 'Manage team members, assign roles, and control module access.',
        steps: 4,
        image: '',
    },
    {
        id: 'regions',
        title: 'Regions',
        description: 'Organize equipment and users by region or job site.',
        steps: 3,
        image: '',
    },
    {
        id: 'skillsets',
        title: 'User Skillsets',
        description: 'Define skill requirements and track employee certifications.',
        steps: 4,
        image: '',
    },
    {
        id: 'inspections',
        title: 'Inspections',
        description: 'Learn how to perform inspections, submit reports, and flag issues.',
        steps: 5,
        image: '',
    },
    {
        id: 'issues',
        title: 'Issues & Discrepancies',
        description: 'Manage flagged issues, track resolution, and assign follow-ups.',
        steps: 3,
        image: '',
    },
];

// ===== FAQ Data (hardcoded) =====
const FAQ_ITEMS = [
    {
        id: 'faq-1',
        question: 'How do I activate a locked module?',
        answer: 'Locked modules can only be activated by a Super Admin. Contact your administrator and ask them to enable the module from the Admin Settings & Billing → Manage Subscriptions dashboard. The module will become available immediately once activated.',
    },
    {
        id: 'faq-2',
        question: "Can I edit an inspection form after it's been used?",
        answer: 'Yes, you can edit inspection forms at any time. However, changes will only apply to future inspections. Previously submitted reports will retain the form structure used at the time of submission.',
    },
    {
        id: 'faq-3',
        question: 'How do I assign a tire to a piece of equipment?',
        answer: 'Navigate to the Equipment module, select the unit, then go to the Tires tab. From there you can assign existing tires from your inventory or add new tires directly to the equipment.',
    },
    {
        id: 'faq-4',
        question: 'What happens when a user is deactivated?',
        answer: 'Deactivated users can no longer log in or perform inspections. Their historical data (reports, inspections, training records) is preserved. You can reactivate them at any time from the Users page.',
    },
    {
        id: 'faq-5',
        question: 'Where do I find old inspection reports?',
        answer: 'Go to the Reports section and select Inspection Reports. You can filter by date range, equipment, user, or form type. Reports are stored indefinitely and can be exported as PDF or CSV.',
    },
    {
        id: 'faq-6',
        question: 'How do I set up Preventive Maintenance schedules?',
        answer: 'Navigate to the PM module and select a piece of equipment. Under the PM Configuration tab you can add service intervals based on time or usage meters. The system will automatically track when services are due.',
    },
    {
        id: 'faq-7',
        question: 'Can I customize email notifications?',
        answer: 'Yes, admins can configure email notifications from the Settings & Billing page. You can control which events trigger emails including inspection submissions, discrepancies, and PM reminders.',
    },
];

export const GetHelpContent = () => {
    const { companyOwner, companyUser, businessAdmin } = useUserContext();
    const [activeTab, setActiveTab] = useState<TabId>('contact-support');
    const [isBugModalOpen, setIsBugModalOpen] = useState(false);
    const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

    const currentUser = companyOwner || companyUser || businessAdmin;
    const companyId = companyOwner?.companyId || companyUser?.companyId || '';
    const userName = currentUser?.name || 'Unknown';

    const handleSubmitBugReport = async (data: { description: string; expected: string; module: string }) => {
        try {
            const noteContent = `[BUG REPORT] Module: ${data.module}\n\nWhat they were trying to do:\n${data.description}\n\nWhat happened instead:\n${data.expected}`;
            await authClient.graphql({
                query: createCompanyNoteMutation,
                variables: {
                    input: {
                        companyId,
                        authorName: userName,
                        content: noteContent,
                    },
                },
            });
            setIsBugModalOpen(false);
            setSubmitSuccess('Bug report submitted successfully!');
            setTimeout(() => setSubmitSuccess(null), 4000);
        } catch (err) {
            console.error('Error submitting bug report:', err);
        }
    };

    const handleSubmitFeatureRequest = async (data: {
        request: string;
        problem: string;
        workaround: string;
        module: string;
        importance: string;
    }) => {
        try {
            const noteContent = `[FEATURE REQUEST] Module: ${data.module} | Importance: ${data.importance}\n\nRequest:\n${data.request}\n\nProblem it would solve:\n${data.problem}\n\nCurrent workaround:\n${data.workaround}`;
            await authClient.graphql({
                query: createCompanyNoteMutation,
                variables: {
                    input: {
                        companyId,
                        authorName: userName,
                        content: noteContent,
                    },
                },
            });
            setIsFeatureModalOpen(false);
            setSubmitSuccess('Feature request submitted successfully!');
            setTimeout(() => setSubmitSuccess(null), 4000);
        } catch (err) {
            console.error('Error submitting feature request:', err);
        }
    };

    return (
        <div className="page-container gh-container">
            <PageHeader
                title="Get Help"
                signalLine="Contact our support team, review module guides, or browse common questions."
            />

            {submitSuccess && <div className="gh-success-banner">✓ {submitSuccess}</div>}

            <div className="tab-component">
                <TabNav
                    id={HELP_TAB_NAV_ID}
                    variant="tabs"
                    ariaLabel="Help topics"
                    flared
                    tabs={TABS}
                    activeTabId={activeTab}
                    onTabChange={(tabId) => {
                        if (VALID_TAB_IDS.has(tabId)) {
                            setActiveTab(tabId as TabId);
                        }
                    }}
                />

                <Card className="tab-body" variant="card-lg" {...getTabPanelProps(HELP_TAB_NAV_ID, activeTab)}>
                    {activeTab === 'contact-support' && (
                        <ContactSupportTab
                            onReportBug={() => setIsBugModalOpen(true)}
                            onRequestFeature={() => setIsFeatureModalOpen(true)}
                        />
                    )}
                    {activeTab === 'module-guides' && <ModuleGuidesTab />}
                    {activeTab === 'faq-docs' && <FaqDocsTab />}
                </Card>
            </div>

            <ReportBugModal
                isOpen={isBugModalOpen}
                onClose={() => setIsBugModalOpen(false)}
                onSubmit={handleSubmitBugReport}
            />
            <RequestFeatureModal
                isOpen={isFeatureModalOpen}
                onClose={() => setIsFeatureModalOpen(false)}
                onSubmit={handleSubmitFeatureRequest}
            />
        </div>
    );
};

// ===== Contact Support Tab =====
const ContactSupportTab = ({
    onReportBug,
    onRequestFeature,
}: {
    onReportBug: () => void;
    onRequestFeature: () => void;
}) => (
    <div className="gh-support-container">
        <h3 className="gh-support-title">Contact TotalEquipIQ</h3>
        <p className="gh-support-subtitle">
            Our support team is available Monday through Friday, 7:00 AM – 6:00 PM Mountain Time
        </p>

        <div className="gh-contact-grid">
            <div className="gh-contact-item">
                <div className="gh-contact-icon">
                    <IconEmail />
                </div>
                <div className="gh-contact-details">
                    <span className="gh-contact-label">Email Us</span>
                    <a href="mailto:support@totalequipiq.com" className="gh-contact-value">
                        support@totalequipiq.com
                    </a>
                    <span className="gh-contact-note">Typically replies within 4 hours</span>
                </div>
            </div>
            <div className="gh-contact-item">
                <div className="gh-contact-icon">
                    <IconPhone />
                </div>
                <div className="gh-contact-details">
                    <span className="gh-contact-label">Phone Support</span>
                    <a href="tel:5552410821" className="gh-contact-value">
                        (555) 241-0821
                    </a>
                    <span className="gh-contact-note">Mon-Fri 7 AM – 6 PM MT</span>
                </div>
            </div>
        </div>

        <div className="gh-action-buttons">
            <Btn
                variant="danger"
                buttonIcon={<IconAlertTriangle />}
                buttonText="Report a Bug"
                buttonOnClick={onReportBug}
            />
            <Btn
                variant="secondary-outlined"
                buttonIcon={<IconAlertCircle />}
                buttonText="Request a Feature"
                buttonOnClick={onRequestFeature}
            />
        </div>
    </div>
);

// ===== Module Guides Tab =====
const ModuleGuidesTab = () => (
    <div className="gh-guides-container">
        <div className="gh-guides-header">
            <h3 className="gh-guides-title">Module Guides</h3>
            <p className="gh-guides-subtitle">
                Review the onboarding walkthrough for any module below for new team members or as a refresher.
            </p>
        </div>
        <div className="gh-guides-grid">
            {MODULE_GUIDES.map((guide) => (
                <div key={guide.id} className="gh-guide-card">
                    <div className="gh-guide-image">
                        {guide.image ? (
                            <img src={guide.image} alt={guide.title} />
                        ) : (
                            <div className="gh-guide-image-placeholder">
                                <span>📷</span>
                            </div>
                        )}
                    </div>
                    <div className="gh-guide-body">
                        <h4 className="gh-guide-title">{guide.title}</h4>
                        <p className="gh-guide-description">{guide.description}</p>
                        <div className="gh-guide-footer">
                            <span className="gh-guide-steps">{guide.steps} steps</span>
                            <button className="gh-guide-link">view guide →</button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// ===== FAQ & Docs Tab =====
const FaqDocsTab = () => (
    <div className="gh-faq-container">
        <div className="gh-faq-header">
            <h3 className="gh-faq-title">Frequently Asked Questions</h3>
            <p className="gh-faq-subtitle">Quick answers to common questions. Looking for something specific?</p>
        </div>
        <AccordionCard
            className="gh-faq-list"
            variant="light"
            expandMode="single"
            items={FAQ_ITEMS.map((faq) => ({
                id: faq.id,
                title: faq.question,
                children: <p className="gh-faq-answer">{faq.answer}</p>,
            }))}
        />
    </div>
);
